/**
 * FeeLadder — the price a fee with early-bird tiers shows: today's price, the
 * full price struck through beside it, and the steps still to come.
 *
 * Presentational only. WHAT it says comes from `feeScheduleModel`
 * (`buildFeeLadder` + `feeLadderTexts`, or `buildFeeLadderTexts` in one call),
 * so this file holds no i18n and no price maths — the same split `cardModel`
 * makes for the card rows. Seven surfaces render it, which is why it is shared
 * rather than drawn per surface.
 *
 *   const { texts } = buildFeeLadderTexts(
 *     { tiers, startDate, timezone, baseAmounts: [groupFees…] },
 *     { t, lang, currency },
 *   );
 *   <FeeLadder texts={texts} />                       // landing, modal: price + steps
 *   <FeeLadder texts={texts} variant="compact" />     // card, calendar: one line
 *
 * Renders the plain price (no strike, no steps) when there is no discount today,
 * and nothing at all when the fee has no price.
 *
 * @see src/competition/feeScheduleModel.js
 */
import React from "react";
import { F } from "../constants";
import { useAppTheme } from "../contexts/ThemeContext";

// Visually hidden but read aloud — the struck-through full price is drawn
// `aria-hidden` and this carries "Full price: …" for screen readers instead.
const SR_ONLY = {
  position: "absolute", width: 1, height: 1, overflow: "hidden",
  clipPath: "inset(50%)", whiteSpace: "nowrap",
};

/**
 * @param {object} p
 * @param {ReturnType<import("../competition/feeScheduleModel").feeLadderTexts>} p.texts
 * @param {"full"|"compact"} [p.variant]  `full` stacks the steps under the price; `compact` is one wrapping line
 * @param {React.CSSProperties} [p.style]  layout only (margin, flex)
 * @param {string} [p.ariaLabel]  names the group for assistive tech (`earlyBird.ladderLabel`)
 */
export default function FeeLadder({ texts, variant = "full", style, ariaLabel }) {
  const { th, accent } = useAppTheme();
  if (!texts || !texts.price) return null;
  const compact = variant === "compact";

  const struck = texts.discounted && texts.fullPrice ? (
    <>
      <span aria-hidden="true" style={{ color: th.textMuted, textDecoration: "line-through", fontWeight: 600 }}>
        {texts.fullPrice}
      </span>
      <span style={SR_ONLY}>{texts.fullPriceAria}</span>
    </>
  ) : null;

  if (compact) {
    return (
      <span role="group" aria-label={ariaLabel} style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "baseline", gap: 6, fontFamily: F, ...style }}>
        <span style={{ fontWeight: 800, color: th.text }}>{texts.price}</span>
        {struck}
        {texts.cardEarly && <span style={{ fontSize: 12, fontWeight: 600, color: th.textMuted }}>{texts.cardEarly}</span>}
      </span>
    );
  }

  return (
    <div role="group" aria-label={ariaLabel} style={{ fontFamily: F, ...style }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: th.text }}>{texts.price}</span>
        {struck}
        {texts.badge && (
          <span style={{
            fontSize: 12, fontWeight: 800, padding: "4px 8px", borderRadius: 999,
            color: accent, background: accent + "22", whiteSpace: "nowrap",
          }}>{texts.badge}</span>
        )}
      </div>
      {texts.steps.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", columnGap: 8, rowGap: 4, marginTop: 6, fontSize: 12, fontWeight: 600, color: th.textMuted, lineHeight: 1.4 }}>
          {texts.steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <span aria-hidden="true">·</span>}
              <span>{s.text}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
